/* eslint-disable no-unused-vars */
import React, { useState, useContext, useEffect } from 'react'
import WidgetTable from '../../components/Widget/WidgetTable/WidgetTable'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useMutation, useQuery } from '@apollo/client'
import { css, StyleSheet } from 'aphrodite'
import { COLOR_WHITE } from '../../lib/colors'
import GET_ALL_REQUEST from './../../graphql/Requests/queries/GetAllRequests'
import { statusWithIcon } from '../../lib/requestStatus'
import { toast } from 'react-toastify'
import UIActions from '../../components/UI/UIActions'
import { COLOR_BLACK } from '../../lib/colors'
import ConfirmationDeleteCommandPopup from '../../modal/ConfirmationDeleteCommandPopup'
import END_REQUEST from '../../graphql/Requests/mutations/EndRequest'
import { useNavigate } from 'react-router-dom'
import LiveContext from '../../components/Context/LiveContext'
import { BP_XS } from '../../lib/breakpoints'
import PlayIcon from '../../assets/play-icon.svg'
import config from '../../config'
import useInterval from '../../hooks/useInterval'

const styles = StyleSheet.create({
  noDataFound: {
    backgroundColor: COLOR_WHITE,
    padding: '20px',
    textAlign: 'center',
  },
  loading: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  underline: {
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
  },
  picto: {
    position: 'relative',
    bottom: '-9px',
    paddingRight: '10px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  overlay: {
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    zIndex: '10',
    top: 0,
    left: 0,
    backgroundColor: `${COLOR_BLACK}30`,
  },
  toast: {
    padding: '20px',
    width: 'fit-content',
  },
})

const RequestsDashboard = () => {
  const { t } = useTranslation('translation')

  const environment = config.REACT_APP_INFRA

  const [isToastOpened, setToastOpened] = useState(false)
  const [actualPage, setActualPage] = useState(0)
  const [tbodyData, setTBodyData] = useState([])
  const [listStartDateLive, setListStartDateLive] = useState([])
  const [liveInTime, setLiveInTime] = useState(null)
  const [pollInterval, setPollInterval] = useState(
    60000 - new Date().getSeconds() * 1000
  )

  const navigate = useNavigate()

  const { idWatcher, toggleLivePreview, toggleLivePreviewMinimized } =
    useContext(LiveContext)

  const [updateRequest] = useMutation(END_REQUEST, {
    onCompleted: () => {
      refetch({
        first: 10,
        offset: actualPage,
        idWatcher,
      })
    },
    onError: () => {
      toast.error(t('pages.viewerAccount.demand.fetchError'), {
        toastId: 'error',
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    },
  })

  const {
    data: requestList,
    loading,
    refetch,
  } = useQuery(GET_ALL_REQUEST, {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 10,
      offset: actualPage,
      idWatcher,
    },
    pollInterval: pollInterval,
    onCompleted: () => {
      setPollInterval(60000 - new Date().getSeconds() * 1000)
    },
    onError: () => {
      toast.error(t('pages.viewerAccount.demand.fetchError'), {
        toastId: 'error',
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    },
  })

  const fetchData = async () => {
    refetch({
      first: 10,
      offset: actualPage,
      idWatcher,
    })
  }

  useEffect(() => {
    fetchData()
  }, [])

  useInterval(() => {
    if (!listStartDateLive.length) return
    const now = new Date()
    listStartDateLive?.forEach((request) => {
      if (
        now >= Date.parse(request.datetimeRange?.start?.value) &&
        now <= Date.parse(request.datetimeRange?.end?.value) - 5 * 6000
      ) {
        setLiveInTime(request.datetimeRange?.start?.value)
      }
    })
  }, 5000)

  const theadData = [
    t('pages.viewerAccount.demand.locations'),
    t('pages.viewerAccount.demand.date'),
    t('pages.viewerAccount.demand.reason'),
    t('pages.viewerAccount.demand.status'),
    t('pages.viewerAccount.demand.link'),
    t('pages.viewerAccount.demand.actions'),
  ]

  useEffect(() => {
    const now = new Date()
    const todayRequests = requestList?.requests?.nodes?.filter(
      (request) =>
        dayjs(now).format('DD/MM/YYYY') ===
        dayjs(request.datetimeRange?.start?.value).format('DD/MM/YYYY')
    )
    if (todayRequests?.length) {
      setListStartDateLive((prev) => [...prev, ...todayRequests])
    }
  }, [requestList])

  useEffect(() => {
    setTBodyData([])
    if (window.innerWidth < BP_XS) return

    const now = new Date()
    const data =
      requestList?.requests?.nodes?.map((item, index) => {
        return {
          id: item.id,
          items: [
            item.requestAddress,
            dayjs(item.datetimeRange?.start?.value).format(
              'DD/MM/YYYY - HH:mm'
            ),
            <p key={index}>{item.title}</p>,
            statusWithIcon(item.status, t(item.status)),
            environment === 'dev' ? (
              <span
                key={index}
                className={css(styles.underline)}
                onClick={() => handleBeginLive(item)}
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                {t('pages.viewerAccount.demand.beginLive')}
              </span>
            ) : (item.status === 'ACCEPTED' || item.status === 'LIVE') &&
              now >= Date.parse(item.datetimeRange?.start?.value) &&
              now <= Date.parse(item.datetimeRange?.end?.value) - 5 * 6000 ? (
              <span
                key={index}
                className={css(styles.underline)}
                onClick={() => handleBeginLive(item)}
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                {t('pages.viewerAccount.demand.beginLive')}
              </span>
            ) : (
              <span
                key={index}
                className={css(styles.underline)}
                style={{ opacity: 0.5 }}
                onClick={() =>
                  toast.info(t('pages.viewerAccount.demand.liveNotInTime'))
                }
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                {t('pages.viewerAccount.demand.beginLive')}
              </span>
            ),
            <UIActions
              key={index}
              handleIconEye={() => handleIconEye(item)}
              handleIconTrash={() => handleIconTrash(item)}
              totalResult={requestList.totalCount}
            />,
          ],
        }
      }) ?? []
    setTBodyData((prev) => [...prev, ...data])
  }, [requestList, liveInTime])

  const toastConfiguration = (id) => {
    return {
      toastId: id,
      position: 'top-center',
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      closeButton: false,
      onOpen: () => setToastOpened(true),
      onClose: () => setToastOpened(false),
      className: css(styles.toast),
    }
  }

  const handleBeginLive = async (item) => {
    localStorage.setItem('idRequest', item.id)
    localStorage.setItem(
      'dateTimeRangeLive',
      JSON.stringify(item.datetimeRange)
    )
    toggleLivePreview(true)
    toggleLivePreviewMinimized(false)
  }

  const handleIconEye = (item) => {
    navigate(`/account/demand/${item.id}`)
  }

  const handleIconTrash = (item) => {
    toast(
      <ConfirmationDeleteCommandPopup
        message={t('pages.viewerAccount.demand.deleteConfirmationMessage')}
        content={t('pages.viewerAccount.demand.deleteCondition')}
        id={item.id}
        handleConfirm={handleDeleteDemand}
      />,
      toastConfiguration(item.id)
    )
  }

  const handleDeleteDemand = (id) => {
    updateRequest({
      variables: {
        input: {
          id: id,
          patch: { status: 'CANCELED' },
        },
      },
    })
    toast.dismiss()
  }

  return (
    <>
      {isToastOpened && <div className={css(styles.overlay)}></div>}
      <div>
        {loading ? (
          <div className={css(styles.loading)}>
            {t('pages.viewerAccount.demand.loading')}
          </div>
        ) : (
          <>
            <WidgetTable
              title={t('pages.viewerAccount.demand.title')}
              theadData={theadData}
              tbodyData={tbodyData}
              totalResult={requestList?.requests?.totalCount}
              onClickNext={async () => {
                refetch({
                  first: 10,
                  offset: actualPage + 10,
                  idWatcher,
                })
                setActualPage((prev) => prev + 10)
              }}
              onClickPrevious={async () => {
                refetch({
                  first: 10,
                  offset: actualPage - 10,
                  idWatcher,
                })
                setActualPage((prev) => prev - 10)
              }}
              hasNextPage={requestList?.requests?.pageInfo?.hasNextPage}
              hasPreviousPage={requestList?.requests?.pageInfo?.hasPreviousPage}
            />
            {tbodyData.length === 0 ? (
              <div className={css(styles.noDataFound)}>
                {t('pages.viewerAccount.demand.noDataFound')}
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  )
}

export default RequestsDashboard
